import React from 'react';
import { Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils/formatters';
import type { ExtraAttributes } from '@/types/service';

interface ExtraAttributeLabel {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date';
}

interface ServiceOrderItem {
  id: string;
  item_type: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  total_price: number | null;
  position: number;
}

interface ServiceOrderEquipmentRow {
  id: string;
  equipment_name: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  reported_issue: string | null;
  diagnosis: string | null;
  accessories: string | null;
  warranty_status: string | null;
  extra_attributes: ExtraAttributes;
  position: number;
  service_order_items: ServiceOrderItem[];
}

interface Props {
  equipment: ServiceOrderEquipmentRow[];
  equipmentLabel: string;
  showExtraAttributes: boolean;
  extraAttributeLabels: ExtraAttributeLabel[];
}

export default function ServiceOrderEquipmentList({
  equipment,
  equipmentLabel,
  showExtraAttributes,
  extraAttributeLabels,
}: Props) {
  if (equipment.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Wrench className="w-8 h-8 mb-2 opacity-40" />
        <p className="text-sm">
          Nenhum {equipmentLabel.toLowerCase()} cadastrado.
        </p>
      </div>
    );
  }

  const sorted = [...equipment].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-4">
      {sorted.map((eq, idx) => (
        <Card key={eq.id}>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              <span className="text-muted-foreground font-normal">
                {equipmentLabel} {idx + 1}
              </span>
              <span>{eq.equipment_name}</span>
              {eq.brand && (
                <Badge variant="secondary">{eq.brand}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {eq.model && (
                <>
                  <span className="text-muted-foreground">Modelo</span>
                  <span>{eq.model}</span>
                </>
              )}
              {eq.serial_number && (
                <>
                  <span className="text-muted-foreground">Nº de Série</span>
                  <span>{eq.serial_number}</span>
                </>
              )}
              {eq.reported_issue && (
                <>
                  <span className="text-muted-foreground">Problema Relatado</span>
                  <span>{eq.reported_issue}</span>
                </>
              )}
              {eq.diagnosis && (
                <>
                  <span className="text-muted-foreground">Diagnóstico</span>
                  <span>{eq.diagnosis}</span>
                </>
              )}
              {eq.accessories && (
                <>
                  <span className="text-muted-foreground">Acessórios</span>
                  <span>{eq.accessories}</span>
                </>
              )}
              {eq.warranty_status && (
                <>
                  <span className="text-muted-foreground">Garantia</span>
                  <span>{eq.warranty_status}</span>
                </>
              )}
            </div>

            {showExtraAttributes &&
              extraAttributeLabels.length > 0 &&
              Object.keys(eq.extra_attributes ?? {}).length > 0 && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm border-t pt-2">
                {extraAttributeLabels.map((attr) =>
                  eq.extra_attributes[attr.key] != null ? (
                    <React.Fragment key={attr.key}>
                      <span className="text-muted-foreground">{attr.label}</span>
                      <span>{String(eq.extra_attributes[attr.key])}</span>
                    </React.Fragment>
                  ) : null,
                )}
              </div>
            )}

            {(eq.service_order_items ?? []).length > 0 && (
              <div className="border-t pt-2">
                <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  Itens
                </p>
                <div className="space-y-1">
                  {[...eq.service_order_items]
                    .sort((a, b) => a.position - b.position)
                    .map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm"
                      >
                        <span>
                          {item.description ?? '—'}
                          {item.quantity !== 1 && (
                            <span className="text-muted-foreground ml-1">
                              × {item.quantity}
                            </span>
                          )}
                        </span>
                        <span className="font-medium">
                          {formatCurrency(item.total_price ?? item.unit_price * item.quantity)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
